import { useEffect, useRef, useState } from 'react';
import type { Chapter } from '@/shared/types';

// Compact chapter-focus filter for the cards review queue. Empty selection
// means "all chapters" (the default). Selection is owned by the parent so
// it can persist to chrome.storage.local.

export function CardFilterBar({
  chapters,
  cardCounts,
  dueCounts,
  selected,
  onChange,
}: {
  chapters: Chapter[];
  // Per-chapter total card count + due-now count, for the row badges.
  cardCounts: Record<string, number>;
  dueCounts: Record<string, number>;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedSet = new Set(selected);
  const ordered = [...chapters].sort((a, b) => a.order - b.order);
  const focusing = selected.length > 0;
  const totalDue = ordered.reduce((n, c) => n + (dueCounts[c.id] ?? 0), 0);

  function toggle(id: string) {
    if (selectedSet.has(id)) {
      onChange(selected.filter((x) => x !== id));
    } else {
      onChange([...selected, id]);
    }
  }

  function clearAll() {
    onChange([]);
  }

  function selectDueOnly() {
    onChange(ordered.filter((c) => (dueCounts[c.id] ?? 0) > 0).map((c) => c.id));
  }

  return (
    <div ref={ref} className="relative border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs">
      <div className="flex flex-col gap-2 @md:flex-row @md:items-center @md:justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setOpen((o) => !o)}
            className={`rounded border px-2 py-1 font-medium ${
              focusing
                ? 'border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100'
                : 'border-slate-300 bg-card text-slate-700 hover:bg-slate-100'
            }`}
          >
            {focusing
              ? `Focused: ${selected.length} of ${ordered.length} chapter${ordered.length === 1 ? '' : 's'}`
              : `All chapters (${ordered.length})`}
            <span className="ml-1 text-slate-400">▾</span>
          </button>
          {focusing && (
            <button
              onClick={clearAll}
              className="text-slate-500 underline-offset-2 hover:underline"
            >
              clear
            </button>
          )}
        </div>
        <span className="font-mono text-slate-500">{totalDue} due now</span>
      </div>

      {open && (
        <div className="absolute left-3 right-3 top-full z-10 mt-1 max-h-72 overflow-auto rounded border border-slate-300 bg-card shadow-lg">
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-2 py-1.5 text-[11px]">
            <span className="font-medium text-slate-700">Pick chapters to focus on</span>
            <div className="flex gap-2">
              <button onClick={selectDueOnly} className="text-blue-700 hover:underline">
                only due
              </button>
              <button onClick={clearAll} className="text-slate-600 hover:underline">
                clear
              </button>
            </div>
          </div>
          <ul className="py-1">
            {ordered.map((c) => {
              const due = dueCounts[c.id] ?? 0;
              const total = cardCounts[c.id] ?? 0;
              const checked = selectedSet.has(c.id);
              return (
                <li key={c.id}>
                  <label className="flex cursor-pointer items-center gap-2 px-2 py-1 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(c.id)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="flex-1 truncate text-slate-800">{c.title}</span>
                    <span className="shrink-0 text-[10px] text-slate-500">
                      {due > 0 ? <span className="text-blue-700">{due} due · </span> : null}
                      {total} card{total === 1 ? '' : 's'}
                    </span>
                  </label>
                </li>
              );
            })}
            {ordered.length === 0 && (
              <li className="px-2 py-2 text-slate-500">No chapters yet.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
